import { portfolioHoldingToComparisonHolding, rollingPerformance } from './utils';
import { getCommonTimestamps, calculateRiskMetrics, trimRollingData } from '../calculation-utils';
import { Interval, SORTING_DIRECTION } from '../constants';
import { HOLDING_TYPES } from '../portfolios';
import { MAX_TIMESTAMP, MIN_TIMESTAMP } from '../time';
import { CURRENCY } from '../currency';
import PortfolioHolding from '@/types/portfolio-holding';
import Holding from '@/types/holding';
import Benchmark from '@/types/benchmark';
import ComparisonHolding from '@/types/comparison-holding';
import Portfolio from '@/types/portfolio';
import Timestamp from '@/types/timestamp';
import PortfolioRisk from '@/types/portfolio-risk';
import Risk from '@/types/risk';
import Fund from '@/types/fund';
import Stock from '@/types/stock';
import CommonInception from '@/types/common-inception';

export const generatePortfolioBenchmarks = (
  portfolioHoldings: PortfolioHolding[],
  data: Holding[],
  benchmarks: Benchmark[],
  showBaseCurrency: boolean,
  overriddenMonthlyRollingsToUse: [Timestamp, number][] = [],
) => {
  const riskFreeBenchmarks: ComparisonHolding[] = [];
  const marketBenchmarks: ComparisonHolding[] = [];
  const holdings: ComparisonHolding[] = [];
  for (const holding of portfolioHoldings) {
    const item = data.find(d => d._id === holding.original.id);
    if (item != null) {
      const isStock = holding.original.type === 'stock';
      const isBenchmark = holding.original.type === 'benchmark';
      /**
       * We need to trim the benchmark within the weighted benchmarks to the same length of the holding
       * so that when the re-balancing occurs for short history the weights shift in the same way
       * if there is no monthly rolling then we simply set the time to the maximum timestamp
       * so that basically there is no corresponding benchmark data for this holding
       */
      // When considering portfolio rollings, ignore holding rollings. Should be used for overridden performance of portfolio
      const monthly = overriddenMonthlyRollingsToUse.length > 0 ? overriddenMonthlyRollingsToUse : item.performance.rolling.monthly;
      const useBaseCurrency = showBaseCurrency && item.currency === CURRENCY.USD;

      const firstTimeStamp = monthly.length > 0 ? monthly[0][0] : MAX_TIMESTAMP;
      const lastTimeStamp = monthly.length > 0 ? monthly[monthly.length - 1][0] : MAX_TIMESTAMP;
      // Typescript hack since the models don't overlap
      const itemAsFund = item as Fund;
      const itemAsStock = item as Stock;

      const newHolding = portfolioHoldingToComparisonHolding(holding, item);
      if (newHolding) {
        holdings.push(newHolding);
      }

      let benchmark;
      let riskFree;

      if (isBenchmark) {
        riskFree = benchmarks.find(b => b._id === itemAsStock.riskFreeId);
      } else {
        riskFree = benchmarks.find(d => d.lipperId === (isStock ? itemAsStock.riskFreeId : itemAsFund.riskFreeLipperId));
        benchmark = benchmarks.find(d => d.lipperId === (isStock ? itemAsStock.benchmarkId : itemAsFund.benchmarkLipperId));
      }

      if (benchmark != null) {
        const rolling = useBaseCurrency ? benchmark.performance.rollingUSD || benchmark.performance.rolling : benchmark.performance.rolling;
        marketBenchmarks.push({
          id: benchmark._id,
          type: HOLDING_TYPES.BENCHMARK,
          name: benchmark.names.short,
          weight: holding.weight,
          rolling: {
            weekly: rolling.weekly,
            monthly: trimRollingData(rolling.monthly, firstTimeStamp, lastTimeStamp),
          },
        });
      }
      if (riskFree != null) {
        const rolling = useBaseCurrency ? riskFree.performance.rollingUSD || riskFree.performance.rolling : riskFree.performance.rolling;
        riskFreeBenchmarks.push({
          id: riskFree._id,
          type: HOLDING_TYPES.BENCHMARK,
          name: riskFree.names?.short || (riskFree as any).name,
          weight: holding.weight,
          rolling: {
            weekly: rolling.weekly,
            monthly: trimRollingData(rolling.monthly, firstTimeStamp, lastTimeStamp),
          },
        });
      }
    }
  }
  return { marketBenchmarks, holdings, riskFreeBenchmarks };
};

// TODO -- This whole method definitely needs to be unit tested.
/**
 * Calculates a portfolio's risk metrics
 * @param {Portfolio} portfolio Portfolio to be calculated
 * @param {[Timestamp, number][]} monthlyRolling the monthly rolling performance for the portfolio
 * @param {Holding[]} data the raw holding data
 * @param {Benchmark[]} benchmarks benchmarks associated to the portfolio
 * @returns `PortfolioRisk` the portfolio risk
 */
const calculatePortfolioRiskMetrics = (
  portfolio: Portfolio,
  portfolioMonthlyRollings: [Timestamp, number][] = [],
  data: Holding[],
  benchmarks: Benchmark[] = [],
  showBaseCurrency: boolean,
  usePortfolioRollingsTimestamps: boolean,
  shortHistory?: boolean,
  commonInception?: CommonInception,
): PortfolioRisk => {
  // Note that shortHistory = true actually means that we have the Show longer history option enabled
  // Note 2 : lastCommonTimestamp includes the benchmarks because we need the last timestamp to be common to all holdings and benchmarks for the risk metrics calculation
  // beginTimestamp must not take into account the benchmarks because some of the risk metrics does not require benchmark to be computed (like std deviation). In the case where
  // we do not have data for benchmarks for the entire period, but we have rollings for the holdings, we will want to have those metrics like std deviation displayed in the comparison builder.
  let beginTimestamp = shortHistory
    ? MIN_TIMESTAMP
    : getCommonTimestamps(
        data.map(item => item.performance.rolling),
        SORTING_DIRECTION.ASC,
      ).monthly;
  let lastCommonTimestamp = getCommonTimestamps(
    [...data, ...benchmarks].map(item => item.performance.rolling),
    SORTING_DIRECTION.DESC,
  ).monthly;

  if (usePortfolioRollingsTimestamps && portfolioMonthlyRollings.length > 0) {
    beginTimestamp = portfolioMonthlyRollings[0][0];
    lastCommonTimestamp = portfolioMonthlyRollings[portfolioMonthlyRollings.length - 1][0];
  }

  const { holdings, marketBenchmarks, riskFreeBenchmarks } = generatePortfolioBenchmarks(
    portfolio.holdings,
    data,
    benchmarks,
    showBaseCurrency,
    usePortfolioRollingsTimestamps ? portfolioMonthlyRollings : [],
  );
  /**
   * If the number of holdings in the benchmarks doesn't line up with the portfolio holding
   * then there is no benchmark (could be a missing/ broken benchmark ref on the holding)
   */
  const benchmarkRollings =
    marketBenchmarks.length === holdings.length ? rollingPerformance(marketBenchmarks, Interval.Monthly, undefined, undefined, shortHistory) : [];
  const riskFreeRollings =
    riskFreeBenchmarks.length === holdings.length ? rollingPerformance(riskFreeBenchmarks, Interval.Monthly, undefined, undefined, shortHistory) : [];
  const portfolioRolling = shortHistory ? portfolioMonthlyRollings : trimRollingData(portfolioMonthlyRollings, beginTimestamp, lastCommonTimestamp);
  let baseRisk = calculateRiskMetrics(portfolioRolling, benchmarkRollings, riskFreeRollings, false, commonInception);

  const standardDeviation = portfolio.override?.standardDeviation != null ? (portfolio.override.standardDeviation as Partial<Risk>) : {};
  baseRisk = {
    ...baseRisk,
    standardDeviation: {
      ...baseRisk.standardDeviation,
      ...standardDeviation,
    },
  };

  return {
    ...baseRisk,
  };
};

export default calculatePortfolioRiskMetrics;
